import type { ICardPaymentMethod } from "../interfaces/payment-method";

export type CardFundingKind = "DEBIT_CARD" | "CREDIT_CARD";

export type UnavailableCardReason =
  | "creditOnDebitRoute"
  | "debitOnCreditRoute"
  | "unclassified"
  | "generic"
  | "railRejected"
  | "railTemporarilyUnavailable";

/** Catalog class only when CVS mapped one. Prepaid / unknown stay null. */
export const cardFundingKind = (
  card: Pick<ICardPaymentMethod, "fundingMethod">
): CardFundingKind | null =>
  card.fundingMethod === "CREDIT_CARD" || card.fundingMethod === "DEBIT_CARD"
    ? card.fundingMethod
    : null;

export const isCardFundingAvailable = (
  card: Pick<ICardPaymentMethod, "fundingMethod">,
  available: readonly string[]
): boolean => {
  const kind = cardFundingKind(card);
  return kind != null && available.includes(kind);
};

export const unavailableCardReason = (
  card: Pick<ICardPaymentMethod, "fundingMethod">,
  available: readonly string[]
): UnavailableCardReason => {
  const kind = cardFundingKind(card);
  if (kind == null) return "unclassified";
  const hasDebit = available.includes("DEBIT_CARD");
  const hasCredit = available.includes("CREDIT_CARD");
  if (kind === "CREDIT_CARD" && hasDebit && !hasCredit) {
    return "creditOnDebitRoute";
  }
  if (kind === "DEBIT_CARD" && hasCredit && !hasDebit) {
    return "debitOnCreditRoute";
  }
  return "generic";
};

/** Maps a pay-time backend code onto the existing unavailable-card copy. */
export const reasonFromCardPayCode = (
  code: string | undefined,
  retryLater?: boolean
): UnavailableCardReason | null => {
  switch (code) {
    case "CARD_CLASS_UNKNOWN":
      return "unclassified";
    case "CARD_NOT_DEBIT":
      return "creditOnDebitRoute";
    case "CARD_NOT_CREDIT":
      return "debitOnCreditRoute";
    case "CARD_PULL_NOT_SUPPORTED":
      return "generic";
    case "CARD_RAIL_UNAVAILABLE":
      return retryLater ? "railTemporarilyUnavailable" : "railRejected";
    default:
      return null;
  }
};
